import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "auto" | "day" | "night";
export type Theme = "day" | "night";

const STORAGE_KEY = "cardash:theme-mode";

function readMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "day" || raw === "night" || raw === "auto") return raw;
  } catch {}
  return "auto";
}

function writeMode(mode: ThemeMode) {
  try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
}

/**
 * useTheme — picks the effective theme from a tri-state mode.
 *
 *   auto  → follow `isDay` (from the weather API; falls back to day)
 *   day   → force light theme
 *   night → force dark theme
 *
 * The effective theme is mirrored onto `<html data-theme="…">` so CSS
 * variables can be overridden via attribute selector.
 */
export function useTheme(isDay: boolean | null) {
  const [mode, setModeState] = useState<ThemeMode>(() => readMode());

  const effective: Theme =
    mode === "day" ? "day" :
    mode === "night" ? "night" :
    isDay === false ? "night" : "day"; // auto + unknown → day

  useEffect(() => {
    document.documentElement.dataset.theme = effective;
    // Hint to the UA for native form controls / scrollbars.
    document.documentElement.style.colorScheme = effective === "day" ? "light" : "dark";
  }, [effective]);

  const setMode = useCallback((m: ThemeMode) => {
    writeMode(m);
    setModeState(m);
  }, []);

  const cycle = useCallback(() => {
    setMode(mode === "auto" ? "day" : mode === "day" ? "night" : "auto");
  }, [mode, setMode]);

  return { mode, effective, setMode, cycle };
}
